import { FormControlLabel, MenuItem, Stack, Switch, TextField } from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../shared/components/FormDialog';
import { DatePickerPtBrField } from '../../../shared/components/DatePickerPtBr';
import { useCrudFormDialog } from '../../../shared/api/useCrudFormDialog';
import { feriadosHooks } from './hooks';
import { feriadoInputSchema, type FeriadoFormValues } from './schemas';
import { FERIADO_TIPOS, type FeriadoDto } from './types';

export interface FeriadoFormDialogProps {
  open: boolean;
  feriado: FeriadoDto | null;
  onClose: () => void;
}

const DEFAULT_VALUES: FeriadoFormValues = {
  data: '',
  nome: '',
  tipo: 'NAC',
  ativo: true,
};

export function FeriadoFormDialog({ open, feriado, onClose }: FeriadoFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const { form, isEdit, onSubmit, serverError } = useCrudFormDialog<
    FeriadoFormValues,
    FeriadoDto,
    FeriadoFormValues,
    string
  >({
    open,
    entity: feriado,
    schema: feriadoInputSchema,
    defaultValues: DEFAULT_VALUES,
    fromEntity: (f) => ({ data: f.data, nome: f.nome, tipo: f.tipo, ativo: f.ativo }),
    getId: (f) => f.data,
    hooks: { useCreate: feriadosHooks.useCreate, useUpdate: feriadosHooks.useUpdate },
    messages: {
      created: t('features.feriados.toasts.created'),
      updated: t('features.feriados.toasts.updated'),
    },
    focusField: 'nome',
    onClose,
  });

  return (
    <FormDialog
      open={open}
      title={isEdit ? t('features.feriados.editTitle') : t('features.feriados.createTitle')}
      form={form}
      onSubmit={onSubmit}
      onCancel={onClose}
      serverError={serverError}
    >
      <Stack spacing={2}>
        <DatePickerPtBrField
          control={form.control}
          name="data"
          label={t('features.feriados.fields.data')}
          disabled={isEdit}
        />
        <TextField
          label={t('features.feriados.fields.nome')}
          fullWidth
          inputProps={{ maxLength: 150 }}
          error={Boolean(form.formState.errors.nome)}
          helperText={form.formState.errors.nome?.message}
          {...form.register('nome')}
        />
        <Controller
          control={form.control}
          name="tipo"
          render={({ field, fieldState }) => (
            <TextField
              select
              fullWidth
              label={t('features.feriados.fields.tipo')}
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              onBlur={field.onBlur}
              inputRef={field.ref}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
            >
              {FERIADO_TIPOS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <Controller
          control={form.control}
          name="ativo"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  checked={field.value ?? true}
                  onChange={(_, checked) => field.onChange(checked)}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                />
              }
              label={t('features.feriados.fields.ativo')}
            />
          )}
        />
      </Stack>
    </FormDialog>
  );
}
